/**
 * Headers, footers, page numbers, Bates numbers and watermarks.
 *
 * The dialog edits the document's whole list of marks and saves it in one
 * go, so adding, changing and removing are a single undo step. Marks stay
 * editable: they are stored in the document, not flattened into it.
 */

import { useEffect, useState } from 'react'
import { api, type PageMark, type SessionInfo } from '../api'

interface Props {
  sessionId: string
  onClose: () => void
  onSaved: (session: SessionInfo) => void
}

const POSITION_LABELS: Record<string, string> = {
  'top-left': 'Top left', 'top-center': 'Top centre', 'top-right': 'Top right',
  'bottom-left': 'Bottom left', 'bottom-center': 'Bottom centre', 'bottom-right': 'Bottom right',
  center: 'Centre (watermark)',
}

const BASE: PageMark = {
  text: '', position: 'bottom-center', fontSize: 10, color: [0, 0, 0], opacity: 1,
  rotation: 0, margin: 36, pages: '', behind: false,
  batesPrefix: '', batesSuffix: '', batesStart: 1, batesDigits: 6,
}

const PRESETS: { label: string; mark: Partial<PageMark> }[] = [
  { label: 'Page numbers', mark: { text: 'Page {page} of {pages}', position: 'bottom-center' } },
  { label: 'Bates numbers', mark: { text: 'CSC-{bates}', position: 'bottom-right', fontSize: 9 } },
  { label: 'Header', mark: { text: '{file}', position: 'top-left', fontSize: 9, color: [0.35, 0.35, 0.35] } },
  { label: 'Date', mark: { text: '{date}', position: 'top-right', fontSize: 9, color: [0.35, 0.35, 0.35] } },
  {
    label: 'DRAFT watermark',
    mark: { text: 'DRAFT', position: 'center', fontSize: 110, rotation: 45, opacity: 0.12, color: [0.75, 0.1, 0.1] },
  },
  {
    label: 'CONFIDENTIAL watermark',
    mark: { text: 'CONFIDENTIAL', position: 'center', fontSize: 72, rotation: 45, opacity: 0.12, color: [0.75, 0.1, 0.1] },
  },
]

const TOKENS: [string, string][] = [
  ['{page}', 'page number'], ['{pages}', 'page count'], ['{date}', 'date added'],
  ['{file}', 'file name'], ['{bates}', 'Bates number'],
]

function toHex([r, g, b]: [number, number, number]): string {
  return '#' + [r, g, b].map((c) => Math.round(c * 255).toString(16).padStart(2, '0')).join('')
}

function fromHex(hex: string): [number, number, number] {
  const n = Number.parseInt(hex.slice(1), 16)
  return [((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255]
}

export function MarksDialog({ sessionId, onClose, onSaved }: Props) {
  const [marks, setMarks] = useState<PageMark[]>([])
  const [selected, setSelected] = useState(0)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const [loaded, setLoaded] = useState(false)

  useEffect(() => {
    void api.marks(sessionId).then((r) => {
      setMarks(r.marks)
      setLoaded(true)
    }).catch((err) => setError(err.message))
  }, [sessionId])

  const current = marks[selected]
  const update = (patch: Partial<PageMark>) =>
    setMarks(marks.map((m, i) => (i === selected ? { ...m, ...patch } : m)))

  const add = (mark: Partial<PageMark>) => {
    setMarks([...marks, { ...BASE, ...mark }])
    setSelected(marks.length)
  }

  const save = async () => {
    setBusy(true)
    setError('')
    try {
      const r = await api.setMarks(sessionId, marks.filter((m) => m.text.trim()))
      onSaved(r.session)
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="modal" role="dialog" aria-modal="true" aria-labelledby="marks-title">
      <div className="modal__panel modal__panel--wide">
        <h2 className="modal__title" id="marks-title">Headers, footers &amp; watermarks</h2>
        <div className="modal__body">
          <div className="marks-presets">
            <span>Add:</span>
            {PRESETS.map((preset) => (
              <button key={preset.label} className="btn btn--small" onClick={() => add(preset.mark)}>
                {preset.label}
              </button>
            ))}
            <button className="btn btn--small" onClick={() => add({ text: 'Text' })}>Custom</button>
          </div>

          {loaded && marks.length === 0 && (
            <p className="modal__hint">No headers, footers or watermarks yet &mdash; add one above.</p>
          )}

          {marks.length > 0 && (
            <div className="marks-layout">
              <ul className="marks-list" role="listbox" aria-label="Marks on this document">
                {marks.map((mark, index) => (
                  <li key={index}>
                    <button role="option" aria-selected={index === selected}
                            className="marks-list__item" onClick={() => setSelected(index)}>
                      <span>{mark.text || '(empty)'}</span>
                      <small>{POSITION_LABELS[mark.position]}{mark.pages ? ` · pages ${mark.pages}` : ''}</small>
                    </button>
                  </li>
                ))}
              </ul>

              {current && (
                <div className="marks-editor">
                  <label className="field">
                    <span>Text</span>
                    <input value={current.text} onChange={(e) => update({ text: e.target.value })} />
                  </label>
                  <div className="token-row">
                    {TOKENS.map(([token, meaning]) => (
                      <button key={token} className="token" title={`Insert the ${meaning}`}
                              onClick={() => update({ text: current.text + token })}>
                        {token}
                      </button>
                    ))}
                  </div>
                  <div className="field-row">
                    <label className="field">
                      <span>Position</span>
                      <select value={current.position} onChange={(e) => update({ position: e.target.value })}>
                        {Object.entries(POSITION_LABELS).map(([value, label]) => (
                          <option key={value} value={value}>{label}</option>
                        ))}
                      </select>
                    </label>
                    <label className="field">
                      <span>Pages</span>
                      <input value={current.pages} placeholder="all"
                             onChange={(e) => update({ pages: e.target.value })} />
                    </label>
                  </div>
                  <div className="field-row">
                    <label className="field">
                      <span>Size (pt)</span>
                      <input type="number" min={4} max={400} value={current.fontSize}
                             onChange={(e) => update({ fontSize: Number(e.target.value) || 10 })} />
                    </label>
                    <label className="field">
                      <span>Colour</span>
                      <input type="color" value={toHex(current.color)}
                             onChange={(e) => update({ color: fromHex(e.target.value) })} />
                    </label>
                    <label className="field">
                      <span>Opacity %</span>
                      <input type="number" min={5} max={100} value={Math.round(current.opacity * 100)}
                             onChange={(e) => update({ opacity: Math.min(1, Math.max(0.05, Number(e.target.value) / 100)) })} />
                    </label>
                    <label className="field">
                      <span>Angle</span>
                      <input type="number" min={-180} max={180} value={current.rotation}
                             onChange={(e) => update({ rotation: Number(e.target.value) || 0 })} />
                    </label>
                  </div>
                  {current.text.includes('{bates}') && (
                    <div className="field-row">
                      <label className="field">
                        <span>Bates start</span>
                        <input type="number" min={0} value={current.batesStart}
                               onChange={(e) => update({ batesStart: Math.max(0, Number(e.target.value) || 0) })} />
                      </label>
                      <label className="field">
                        <span>Digits</span>
                        <input type="number" min={1} max={12} value={current.batesDigits}
                               onChange={(e) => update({ batesDigits: Math.min(12, Math.max(1, Number(e.target.value) || 6)) })} />
                      </label>
                    </div>
                  )}
                  <label className="check">
                    <input type="checkbox" checked={current.behind}
                           onChange={(e) => update({ behind: e.target.checked })} />
                    Behind the page&rsquo;s content
                  </label>
                  <button className="linkbtn linkbtn--danger" onClick={() => {
                    setMarks(marks.filter((_, i) => i !== selected))
                    setSelected(Math.max(0, selected - 1))
                  }}>
                    Remove this one
                  </button>
                </div>
              )}
            </div>
          )}

          <p className="modal__hint">
            Drawn on top of the document without touching its content, and
            kept up to date: page numbers follow when pages are added, removed
            or reordered. Search and screen readers skip them, as they should.
          </p>
          {error && <p className="modal__hint modal__hint--warn">{error}</p>}
        </div>
        <div className="modal__footer">
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn btn--primary" disabled={busy || !loaded} onClick={() => void save()}>
            {busy ? 'Applying…' : 'Apply'}
          </button>
        </div>
      </div>
    </div>
  )
}
